<template>
  <v-card class="added-to-basket-dialog" :class="{'is-dragging': dragging}" :style="sheetStyle"
          @touchstart.native="onTouchStart" @touchmove.native="onTouchMove" @touchend.native="onTouchEnd" @touchcancel.native="onTouchEnd"
          @wheel.native="onWheel" @keydown.native="onKeydown">
    <div v-if="mobile" class="sheet-handle" aria-hidden="true"><span/></div>
    <v-card-title ref="header" class="dialog-header">
      <v-icon color="primary" class="confirmation-icon" aria-hidden="true">check_circle</v-icon>
      <div class="confirmation-message">
        <h2 class="text-h6">{{ $t(upgraded ? 'PRODUCT_UPGRADED_IN_BASKET' : (localQuantity > 1 ? 'PRODUCTS_ADDED_TO_BASKET' : 'PRODUCT_ADDED_TO_BASKET')) }}</h2>
      </div>
      <v-btn ref="closeButton" icon :aria-label="$t('BUTTON_CONTINUE_SHOPPING')" @click="$emit('backToProducts')">
        <v-icon>close</v-icon>
      </v-btn>
    </v-card-title>
    <div ref="content" class="v-card__text dialog-content" tabindex="0">
      <div ref="contentBody" class="dialog-body">
      <div class="added-products" tabindex="0">
        <div v-for="(item, index) in displayedItems" :key="index" class="added-product">
          <v-img :src="itemProduct(item).imageUrl" :alt="itemProduct(item).name" contain width="72" height="72" class="product-image"/>
          <div class="product-info">
            <h3>{{ itemProduct(item).name }}</h3>
            <ProductPrice :price="unitPrice(item)" :instead-of="itemProduct(item).setPriceInsteadOf"/>
          </div>
          <div class="quantity-controls" :aria-label="$t('BASKET_HEADER_AMOUNT')">
            <span class="quantity-label">{{ $t('BASKET_HEADER_AMOUNT') }}</span>
            <v-btn icon small :disabled="busy || item.amount === 0"
                   :aria-label="`${$t('BASKET_HEADER_AMOUNT')} −1`" @click="changeQuantity(item, item.amount - 1)">
              <v-icon small>remove</v-icon>
            </v-btn>
            <input :value="item.amount" type="number" min="0" :max="maximumAmount(item)" step="1" inputmode="numeric"
                   :disabled="busy" :aria-label="$t('BASKET_HEADER_AMOUNT')"
                   @change="onQuantityInput(item, $event)" @keydown.enter="$event.target.blur()"/>
            <v-btn icon small :disabled="busy || item.amount >= maximumAmount(item)"
                   :aria-label="`${$t('BASKET_HEADER_AMOUNT')} +1`" @click="changeQuantity(item, item.amount + 1)">
              <v-icon small>add</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      <DiscountBanner :compact="mobile" detached-details class="confirmation-discount"/>
      <section v-if="recommendation" class="recommended-product" :style="recommendationStyle"
               :aria-label="recommendation.name">
        <div class="recommendation-visual">
          <v-img :src="recommendation.imageUrl" :alt="recommendation.name" contain class="recommendation-image"/>
        </div>
        <div class="recommendation-details">
          <div v-if="hasBasketQuantity" class="recommendation-delta-headline">
            {{ recommendationDeltaHeadline }}
          </div>
          <div class="recommendation-heading">
            <h3 class="recommendation-title">{{ recommendation.name }}</h3>
            <span class="recommendation-label">{{ $t('UPSELL_RECOMMENDATION') }}</span>
          </div>
          <ul v-if="recommendationPoints.length" class="recommendation-points" tabindex="0">
            <li v-for="(point, index) in recommendationPoints" :key="index">
              <v-icon small color="primary" aria-hidden="true">add</v-icon><span>{{ point }}</span>
            </li>
          </ul>
        </div>
        <div class="recommendation-actions" :class="mobile ? 'recommendation-mobile-actions' : 'recommendation-desktop-actions'">
          <ProductPrice v-if="!hasBasketQuantity" class="recommendation-price" :price="recommendation.price"
                        :instead-of="recommendation.setPriceInsteadOf"
                        :from="recommendation.detailedConfigurationSurcharge > 0"/>
          <v-btn color="primary" class="recommendation-action" @click="configureRecommendation">
            <v-icon left small>add</v-icon>
            {{ recommendationAction }}
          </v-btn>
          <div v-if="hasBasketQuantity" class="recommendation-delta-label">{{ $t('UPSELL_DELTA_PER_ITEM') }}</div>
        </div>
      </section>
      <v-alert v-if="quantityError" type="error" dense role="alert">{{ quantityError }}</v-alert>
      <v-dialog v-model="replacementChoice" max-width="480">
        <v-card class="replacement-dialog">
          <v-card-title class="replacement-header">
            <span>{{ $t('UPSELL_REPLACE_QUANTITY_TITLE') }}</span>
            <v-btn icon :aria-label="$vuetify.lang.t('$vuetify.close')" @click="replacementChoice = false"><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text v-if="sourceItems.length">
            <v-checkbox v-for="item in sourceItems" :key="item.position.id"
                        v-model="selectedPositionIds" :value="item.position.id"
                        :aria-label="itemProduct(item).name" hide-details>
              <template #label>
                <div class="replacement-position">
                  <span>{{ itemProduct(item).name }}</span>
                  <span class="replacement-quantity">×{{ item.amount }}</span>
                  <div class="replacement-images">
                    <v-img v-for="image in previewImages(item)" :key="image.uniqueId" :src="image.smallUrl"
                           :alt="image.name" contain width="48" max-width="48" height="48"/>
                  </div>
                </div>
              </template>
            </v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-btn v-if="!hasMultipleSelectedUnits" color="primary" :disabled="!selectedSourceItems.length"
                   @click="startRecommendation('all')">{{ $t('UPSELL_UPGRADE_PRODUCT') }}</v-btn>
            <template v-else>
              <v-btn text color="primary" @click="startRecommendation('one')">{{ $t(selectedSourceItems.length > 1 ? 'UPSELL_REPLACE_EACH' : 'UPSELL_REPLACE_ONE') }}</v-btn>
              <v-btn color="primary" @click="startRecommendation('all')">{{ $t('UPSELL_REPLACE_ALL') }}</v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
      </div>
    </div>
    <v-divider/>
    <v-card-actions ref="actions" class="dialog-actions">
      <v-btn text small class="configure-again" @click="$emit('configureAgain')">
        <v-icon left small>replay</v-icon>
        {{ $t("BUTTON_CONFIGURE_AGAIN") }}
      </v-btn>
      <v-spacer class="hidden-sm-and-down"/>
      <v-btn color="grey lighten-2" to="/basket">
        <v-icon left>shopping_cart</v-icon>
        {{ $t("GO_TO_BASKET") }}
      </v-btn>
      <v-btn color="primary" class="continue-shopping" @click="$emit('backToProducts')">
        {{ $t("BUTTON_CONTINUE_SHOPPING") }}
        <v-icon right>arrow_forward</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import Vue from 'vue';
import BasicComponent from '@/global/BasicComponent';
import {Prop, Watch} from 'vue-property-decorator';
import {BasketApi, BasketDto, ImageDto, ProductApi, ProductDto, ProductMediumDto, ProductRecommendationDto, ProductTypeDto} from '@swagger';
import {AddedBasketItem} from '@/global/AddedBasketItem';
import {sourcePersonCodes} from '@/global/ProductConfigurationPrefill';
import DiscountBanner from '@/components/DiscountBanner.vue';
import ProductPrice from '@/components/ProductPrice.vue';
import {MAX_DIGITAL_POSITION_AMOUNT, MAX_POSITION_AMOUNT} from '@/global/BasketLimits';

type ReplacementQuantityMode = 'one' | 'all';

@Component({components: {DiscountBanner, ProductPrice}})
export default class AddedToBasketDialog extends BasicComponent {
  @Prop(Object) product: ProductDto;
  @Prop(Array) items: AddedBasketItem[];
  @Prop(Boolean) upgraded: boolean;
  displayedItems: AddedBasketItem[] = [];
  busy = false;
  quantityError = '';
  prices: {[id: number]: number} = {};
  recommendationData: ProductRecommendationDto | null = null;
  recommendationCandidates: ProductRecommendationDto[] = [];
  replacementChoice = false;
  selectedPositionIds: number[] = [];
  disposed = false;
  quantityUpdate: Promise<boolean> | null = null;
  recommendationPending = false;
  recommendationStarted = false;

  created() {
    // Keep the canonical basket amount beside the local contribution. Existing
    // units on a merged line never become part of this confirmation flow.
    const basket: BasketDto = this.$store.getters.basket;
    const unique = new Map(this.items.map(item => [item.position.id, item]));
    this.displayedItems = Array.from(unique.values()).map(item => {
      const position = basket.positions.find(position => position.id === item.position.id) ?? item.position;
      this.prices[position.id] = position.amount ? position.totalPrice / position.amount
        : (this.$store.getters.product(item.configuration.productId)?.price ?? 0);
      return {...item, position: {...position}};
    });
    this.selectedPositionIds = this.sourceItems.map(item => item.position.id);
    this.loadRecommendation();
  }

  async loadRecommendation() {
    try {
      const recommendations = await new ProductApi(this.apiConfiguration).getProductRecommendations(this.product.id);
      if (!this.disposed) {
        this.recommendationCandidates = recommendations;
        this.updateRecommendation();
      }
    } catch (error) {
      // Recommendations are optional; a failed request must not interrupt the
      // successful addition or the customer's basket controls.
      console.warn('Unable to load product recommendations', error);
    }
  }

  @Watch('sourceItems', {deep: true})
  updateRecommendation() {
    // The endpoint ranks products for the session, which may contain several
    // codes. An upgrade must also work for each actual source selection, using
    // the same person restriction as the replacement configurator.
    this.recommendationData = this.recommendationCandidates.find(({product}) =>
      product.id !== this.product.id && this.sourceItems.every(item => {
        const codes = sourcePersonCodes([item.configuration], this.allPersons);
        const chosenCode = item.choices?.personCode;
        return this.allPersons.some(person => codes.includes(person.code)
          && (!chosenCode || person.code === chosenCode)
          && person.productIds.includes(product.id)
          && product.imageRequirements.every(requirement => this.getPersonImages(person)
            .some(image => this.imageMatchesRequirement(image, requirement))));
      })) ?? null;
  }

  beforeDestroy() {
    this.disposed = true;
    this.sheetObserver?.disconnect();
    window.removeEventListener('resize', this.measureSheet);
    window.visualViewport?.removeEventListener('resize', this.measureSheet);
  }

  get hasBasketQuantity(): boolean {
    return this.sourceQuantity > 0;
  }

  get recommendation(): ProductDto | null {
    return this.recommendationData?.product ?? null;
  }

  get sourceItems(): AddedBasketItem[] {
    return this.displayedItems.filter(item => item.amount > 0 && item.configuration.productId === this.product.id);
  }

  get sourceQuantity(): number {
    return this.sourceItems.reduce((total, item) => total + item.amount, 0);
  }

  get selectedSourceItems(): AddedBasketItem[] {
    return this.sourceItems.filter(item => this.selectedPositionIds.includes(item.position.id));
  }

  get hasMultipleSelectedUnits(): boolean {
    return this.selectedSourceItems.some(item => item.amount > 1);
  }

  get localQuantity(): number {
    return this.displayedItems.reduce((total, item) => total + item.amount, 0);
  }

  get recommendationAction(): string {
    if (this.hasBasketQuantity) {
      return this.priceMessage('UPSELL_DELTA_CTA');
    }
    return this.$t('BUTTON_ADD_PRODUCT').toString();
  }

  get recommendationDeltaCents(): number {
    return Math.min(...this.recommendationDeltasCents);
  }

  get recommendationDeltasCents(): number[] {
    if (!this.recommendation) {
      return [0];
    }
    const targetBaseCents = Math.round(this.recommendation.price * 100);
    const sources = this.sourceItems;
    if (!sources.length) {
      return [targetBaseCents - Math.round(this.product.price * 100)];
    }
    return sources.map(item => {
      const sourceProduct = this.itemProduct(item);
      const sourceBaseCents = Math.round(sourceProduct.price * 100);
      const sourceSurcharge = item.position.configurationSurcharge !== null
        && item.position.configurationSurcharge !== undefined
        ? item.position.configurationSurcharge
        : (item.configuration.detailedConfiguration ? sourceProduct.detailedConfigurationSurcharge : 0);
      const sourceSurchargeCents = Math.round((sourceSurcharge ?? 0) * 100);
      // Apply the same configuration mode to the target. Coupon-adjusted row
      // totals are deliberately irrelevant to this per-item price comparison.
      const targetSurchargeCents = item.configuration.detailedConfiguration
        ? Math.round((this.recommendation.detailedConfigurationSurcharge ?? 0) * 100) : 0;
      return targetBaseCents + targetSurchargeCents - sourceBaseCents - sourceSurchargeCents;
    });
  }

  get recommendationDeltaPrice(): string {
    const cents = this.recommendationDeltasCents;
    const min = Math.min(...cents);
    const max = Math.max(...cents);
    const format = (value: number) => this.formatPrice(value / 100);
    return min === max ? format(min) : `${format(min)}–${format(max)}`;
  }

  get recommendationDeltaHeadline(): string {
    if (!this.hasBasketQuantity) {
      return '';
    }
    return this.priceMessage('UPSELL_DELTA_HEADLINE');
  }

  priceMessage(key: string): string {
    return this.$t(key).toString().replace('{price}', this.recommendationDeltaPrice);
  }

  async configureRecommendation() {
    if (this.recommendationPending || this.recommendationStarted || this.disposed || !this.recommendation) {
      return;
    }
    this.recommendationPending = true;
    try {
      // A quantity input blurs before this click. Wait for that exact save and
      // derive replacement amounts only from its confirmed server response.
      if (this.quantityUpdate && !await this.quantityUpdate) {
        return;
      }
      if (this.disposed || this.quantityError) {
        return;
      }
      this.selectedPositionIds = this.sourceItems.map(item => item.position.id);
      if (this.sourceQuantity > 1) {
        this.replacementChoice = true;
      } else {
        this.startRecommendation('all');
      }
    } finally {
      this.recommendationPending = false;
    }
  }

  startRecommendation(mode: ReplacementQuantityMode) {
    if (this.disposed || this.recommendationStarted || this.busy || this.quantityError || !this.recommendation) {
      return;
    }
    const sources = this.selectedSourceItems;
    // A selection may become stale while the quantity dialog is closed. Never
    // turn an intended replacement into an ordinary add.
    if (this.sourceQuantity > 0 && (!sources.length || sources.some(item => item.amount < 1))) {
      this.quantityError = this.$t('UPSELL_REPLACEMENT_UNAVAILABLE').toString();
      this.replacementChoice = false;
      return;
    }
    const replacements = sources.map(item => {
      const replaceAmount = mode === 'one' ? 1 : item.amount;
      return {positionId: item.position.id, sourceProductId: this.product.id,
        amount: replaceAmount, expectedAmount: item.position.amount};
    }).filter(source => source.amount > 0);
    this.replacementChoice = false;
    this.recommendationStarted = true;
    this.$emit('configureProduct', {product: this.recommendation, replacements,
      items: this.displayedItems.map(item => ({...item, position: {...item.position}}))});
  }

  itemProduct(item: AddedBasketItem): ProductDto {
    return this.$store.getters.product(item.configuration.productId) ?? this.product;
  }

  previewImages(item: AddedBasketItem): ImageDto[] {
    const uniqueIds = Array.from(new Set(item.position.product.images.map(image => image.uniqueImageId)));
    return uniqueIds.map(id => this.$store.getters.imageById(id)).filter(Boolean);
  }

  get recommendationPoints(): string[] {
    return (this.recommendationData?.additions ?? []).map(addition =>
      `${addition.quantity > 1 && !addition.downloadAll ? `${addition.quantity}× ` : ''}${addition.name}`);
  }

  get recommendationStyle() {
    const primary = this.$vuetify.theme.currentTheme.primary;
    return {'--recommendation-color': String(primary)};
  }

  unitPrice(item: AddedBasketItem): number {
    return this.prices[item.position.id] ?? this.itemProduct(item).price ?? 0;
  }

  maximumAmount(item: AddedBasketItem): number {
    const product = this.itemProduct(item);
    const digital = product.productType === ProductTypeDto.SET
      ? product.detailedImageRequirements.every(requirement => requirement.productMedium === ProductMediumDto.DOWNLOAD)
      : product.medium === ProductMediumDto.DOWNLOAD;
    const canonical = this.$store.getters.basket.positions.find(position => position.id === item.position.id);
    const baseline = Math.max(0, (canonical?.amount ?? 0) - item.amount);
    return Math.max(0, (digital ? MAX_DIGITAL_POSITION_AMOUNT : MAX_POSITION_AMOUNT) - baseline);
  }

  async onQuantityInput(item: AddedBasketItem, event: Event) {
    const input = event.target as HTMLInputElement;
    const amount = Number(input.value);
    if (input.value.trim() && Number.isInteger(amount) && amount >= 0 && amount <= this.maximumAmount(item)) {
      await this.changeQuantity(item, amount);
    }
    input.value = String(item.amount);
  }

  changeQuantity(item: AddedBasketItem, amount: number): Promise<boolean> {
    if (this.busy) {
      return this.quantityUpdate ?? Promise.resolve(false);
    }
    if (amount === item.amount) {
      return Promise.resolve(!this.quantityError);
    }
    this.busy = true;
    this.quantityError = '';
    this.quantityUpdate = this.saveQuantity(item, amount).finally(() => {
      this.busy = false;
      this.quantityUpdate = null;
    });
    return this.quantityUpdate;
  }

  async saveQuantity(item: AddedBasketItem, amount: number): Promise<boolean> {
    try {
      const api = new BasketApi(this.apiConfiguration);
      if (item.amount === 0) {
        // Re-add the original configuration, rather than an unconfigured product.
        await this.addArticleToBasket(item.configuration.productId, item.configuration.images,
            item.configuration.detailedConfiguration, restored => {
              if (restored) {
                const price = this.unitPrice(item);
                item.position = {...restored.position};
                item.amount = restored.amount;
                this.$set(this.prices, item.position.id, price);
              }
            }, error => { this.quantityError = String(error); });
        if (item.amount === 0 || this.quantityError) {
          return false;
        }
        if (amount === item.amount) {
          return true;
        }
      }
      const canonical = this.$store.getters.basket.positions.find(position => position.id === item.position.id);
      const baseline = Math.max(0, (canonical?.amount ?? 0) - item.amount);
      const targetAmount = baseline + amount;
      const data = await api.editPositionAmount({positionId: item.position.id, amount: targetAmount});
      this.$store.commit('setBasket', data);
      const updated = data.positions.find(position => position.id === item.position.id);
      if (updated) {
        item.position = {...updated};
        item.amount = Math.max(0, updated.amount - baseline);
        this.$set(this.prices, updated.id, updated.totalPrice / updated.amount);
      } else {
        item.position = {...item.position, amount: 0, totalPrice: 0};
        item.amount = 0;
      }
      return true;
    } catch (error) {
      const detail = typeof error.json === 'function' ? await error.json().catch(() => null) : null;
      this.quantityError = detail?.message ?? String(error.message ?? error);
      return false;
    }
  }
  expansion = 0;
  dragging = false;
  touchY = 0;
  sheetBaseHeight = 0;
  sheetViewportHeight = 0;
  sheetObserver: ResizeObserver | null = null;

  mounted() {
    this.$nextTick(() => {
      if (this.disposed) {
        return;
      }
      ((this.$refs.closeButton as Vue)?.$el as HTMLElement)?.focus();
      this.syncSheetObserver();
    });
    window.addEventListener('resize', this.measureSheet);
    window.visualViewport?.addEventListener('resize', this.measureSheet);
  }

  syncSheetObserver() {
    this.sheetObserver?.disconnect();
    this.sheetObserver = null;
    if (!this.mobile || this.disposed) {
      return;
    }
    // Gesture expansion needs the natural mobile content height, including async recommendations.
    // Observe unconstrained content so changing the sheet height cannot feed back into this measurement.
    this.sheetObserver = new ResizeObserver(this.measureSheet);
    this.sheetObserver.observe(this.$refs.contentBody as HTMLElement);
    this.sheetObserver.observe(this.$refs.header as HTMLElement);
    this.sheetObserver.observe(this.$refs.actions as HTMLElement);
    this.measureSheet();
  }

  measureSheet() {
    const content = this.$refs.content as HTMLElement;
    const body = this.$refs.contentBody as HTMLElement;
    if (!content || !body || !this.mobile || this.disposed) {
      return;
    }
    const viewport = window.visualViewport?.height ?? window.innerHeight;
    const padding = getComputedStyle(content);
    const chrome = Array.from(this.$el.children)
      .filter(element => element !== content)
      .reduce((height, element) => height + (element as HTMLElement).offsetHeight, 0);
    const naturalHeight = body.scrollHeight + parseFloat(padding.paddingTop) + parseFloat(padding.paddingBottom) + chrome;
    this.sheetViewportHeight = viewport;
    this.sheetBaseHeight = Math.min(Math.ceil(naturalHeight), Math.round(viewport * 0.8));
    if (naturalHeight <= this.sheetBaseHeight + 1) this.expansion = 0;
  }

  get mobile(): boolean {
    return this.$vuetify.breakpoint.smAndDown;
  }

  @Watch('mobile')
  onMobileChanged() {
    // The handle and mobile footer layout must be rendered before measuring.
    this.$nextTick(this.syncSheetObserver);
  }

  get sheetStyle() {
    if (!this.mobile) {
      return {};
    }
    return this.sheetBaseHeight ? {
      height: `${this.sheetBaseHeight + this.expansion * (this.sheetViewportHeight - this.sheetBaseHeight)}px`,
      maxHeight: `${this.sheetViewportHeight}px`
    } : {maxHeight: '80dvh'};
  }

  // Consume upward scrolling to expand first. Once full height, let the content
  // scroll normally; only collapse on downward scrolling at its top edge.
  resizeSheet(delta: number): boolean {
    if (!this.mobile || !delta) {
      return false;
    }
    const content = this.$refs.content as HTMLElement;
    if (delta > 0 && this.expansion >= 1) {
      return false;
    }
    if (delta > 0 && (!content || content.scrollHeight <= content.clientHeight + 1)) {
      return false;
    }
    if (delta < 0 && (this.expansion <= 0 || content?.scrollTop > 0)) {
      return false;
    }
    const travel = this.sheetViewportHeight - this.sheetBaseHeight;
    if (travel <= 0) {
      return false;
    }
    this.expansion = Math.max(0, Math.min(1, this.expansion + delta / travel));
    return true;
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) {
      return;
    }
    this.touchY = event.touches[0].clientY;
    this.dragging = true;
  }

  onTouchMove(event: TouchEvent) {
    if (!this.dragging || event.touches.length !== 1) {
      return;
    }
    const y = event.touches[0].clientY;
    const delta = this.touchY - y;
    this.touchY = y;
    if (this.canScrollItems(event.target, delta)) {
      return;
    }
    if (event.cancelable && this.resizeSheet(delta)) event.preventDefault();
  }

  canScrollItems(target: EventTarget | null, delta: number): boolean {
    const list = target instanceof Element ? target.closest('.recommendation-points, .added-products') : null;
    if (!list || list.scrollHeight <= list.clientHeight + 1) {
      return false;
    }
    return delta > 0 ? list.scrollTop + list.clientHeight < list.scrollHeight - 1 : list.scrollTop > 0;
  }

  onTouchEnd() {
    this.dragging = false;
  }

  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      return;
    }
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    if (this.canScrollItems(event.target, event.deltaY)) {
      return;
    }
    if (this.resizeSheet(event.deltaY * unit)) event.preventDefault();
  }

  onKeydown(event: KeyboardEvent) {
    if (event.target !== this.$refs.content) {
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'PageDown') {
      if (this.resizeSheet(event.key === 'PageDown' ? window.innerHeight : 40)) event.preventDefault();
    } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
      if (this.resizeSheet(event.key === 'PageUp' ? -window.innerHeight : -40)) event.preventDefault();
    }
  }
}
</script>

<style lang="scss" scoped>
@use "../../stylesheets/Variables.scss" as *;
.added-to-basket-dialog {
  display: flex;
  flex-direction: column;
  min-height: 0;

  .dialog-header {
    flex-shrink: 0;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 16px;

    .confirmation-message {
      flex: 1;
      min-width: 0;
    }

    h2 {
      overflow-wrap: normal;
      word-break: normal;
      hyphens: none;
    }


    .v-btn {
      flex-shrink: 0;
    }
    .confirmation-icon {
      flex-shrink: 0;
      align-self: flex-start;
      margin-top: 6px;
    }
  }

  .dialog-content {
    display: flex;
    flex-direction: column;
    min-height: 240px;
    overflow-y: auto;
    overscroll-behavior-y: contain;
  }
  .dialog-body {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    min-width: 0;
  }
  .dialog-body > * {
    flex-shrink: 0;
  }

  .confirmation-discount {
    width: 100%;
    margin: 0 0 10px;
  }
  .added-products {
    max-height: 240px;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    padding: 6px 8px;
    margin: 0 -8px 8px;
  }
  .added-product {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-bottom: 8px;
    color: $shop-black;
    background: $shop-white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba($shop-black, .14);
    &:last-child {
      margin-bottom: 0;
    }
  }

  .product-image {
    flex: 0 0 72px;
  }
  .product-info {
    flex: 1 1 120px;
    min-width: 0;
  }
  .product-info h3 {
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 4px;
    word-break: normal;
  }
  .quantity-controls {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
  }
  .quantity-label {
    font-size: 12px;
    margin-right: 4px;
  }
  .quantity-controls input {
    width: 44px;
    text-align: center;
    border-bottom: 1px solid $shop-input-border;
    appearance: textfield;
  }
  .quantity-controls input::-webkit-inner-spin-button,
  .quantity-controls input::-webkit-outer-spin-button {
    appearance: none;
    margin: 0;
  }

  .recommended-product {
    position: relative;
    isolation: isolate;
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr) auto;
    grid-template-areas: 'image details actions';
    grid-template-rows: auto;
    align-items: start;
    gap: 12px 16px;
    padding: 0;
    margin-top: 8px;
    border: 3px solid transparent;
    background: linear-gradient($shop-white, $shop-white) padding-box,
      linear-gradient(120deg, rgba($shop-highlight-gold, .6), rgba($shop-highlight-orange, .6), rgba($shop-highlight-pink, .6), rgba($shop-highlight-violet, .6)) border-box;
    box-shadow: 0 2px 10px rgba($shop-highlight-violet, .18);
    border-radius: 4px;

  }
  .recommendation-visual {
    grid-area: image;
    position: relative;
    width: 160px;
    height: 160px;
    overflow: hidden;
    border-radius: 1px 0 0 1px;
  }
  .recommendation-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0 !important;
  }
  .recommendation-details {
    grid-area: details;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    min-width: 0;
    padding: 16px 0;
  }
  .recommendation-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }
  .recommendation-label {
    position: absolute;
    top: -2px;
    right: 12px;
    transform: translateY(-50%);
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    color: white;
    background: color-mix(in srgb, var(--recommendation-color) 75%, black);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
  }
  .recommendation-title {
    flex: 1;
    min-width: 0;
    margin: 0;
    color: $shop-black;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    word-break: normal;
    overflow-wrap: anywhere;
  }
  .recommendation-delta-headline {
    margin: 0;
    color: $shop-black;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
  }
  .recommendation-price {
    justify-self: end;
    padding: 0 16px 0 0;
  }
  .recommendation-points {
    list-style: none;
    width: 100%;
    max-height: 144px;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    padding: 0;
    margin: 0;
    color: rgba($shop-black, .87);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;

    li {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin: 4px 0;
    }
    .v-icon {
      flex-shrink: 0;
      margin-top: 3px;
    }
  }
  .recommendation-actions {
    grid-area: actions;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    align-self: end;
    gap: 4px;
    min-width: 0;
    margin: 0 16px 6px 0;
  }
  .recommendation-action {
    justify-self: end;
    align-self: center;
    margin: 0;
    letter-spacing: 0;
    max-width: 100%;
    min-width: 0;
    height: auto;
    min-height: 36px;
  }
  .recommendation-action ::v-deep .v-btn__content {
    white-space: normal;
    padding: 8px 0;
  }
  .recommendation-delta-label {
    width: 100%;
    text-align: center;
    font-size: 12px;
    line-height: 1.3;
    color: rgba($shop-black, .65);
  }

  .dialog-actions {
    flex-shrink: 0;
    padding: 16px 24px;
    gap: 12px;

    .v-btn + .v-btn, .v-btn {
      margin: 0;
    }
  }

  @media (min-width: 960px) {
    .recommended-product {
      grid-template-columns: 160px minmax(0, 1fr) auto;
      grid-template-areas: 'image details actions';
      grid-template-rows: auto;
      margin-top: 8px;
      border-width: 5px;
    }
    .recommendation-details {
      padding: 16px 0;
    }
    .recommendation-desktop-actions {
      align-items: center;
      justify-self: end;
    }
  }

  @media (max-width: 959px) {
    height: auto;
    max-height: 100dvh;
    border-radius: 0;
    transition: height 160ms ease-out;
    animation: sheet-enter 240ms ease-out;

    &.is-dragging {
      transition: none;
    }

    .sheet-handle {
      flex: 0 0 20px;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        width: 32px;
        height: 4px;
        border-radius: 2px;
        background: $shop-disabled;
      }
    }

    .dialog-header {
      padding: 0 12px 12px;
      gap: 8px;
    }

    .added-product {
      display: grid;
      grid-template-columns: 56px minmax(0, 1fr) auto;
      gap: 8px;
      padding: 8px;
      margin-bottom: 6px;
    }
    .product-image {
      width: 56px !important;
      height: 56px !important;
    }
    .product-info h3 {
      font-size: 14px;
      line-height: 1.25;
      margin-bottom: 2px;
    }

    .quantity-controls {
      display: grid;
      grid-template-columns: 28px 36px 28px;
      gap: 2px;
      justify-items: center;
    }
    .quantity-controls input {
      width: 36px;
    }
    .quantity-label {
      grid-column: 1 / -1;
      margin-right: 0;
      white-space: nowrap;
    }

    .dialog-content {
      padding-inline: 12px;
      flex: 1 1 auto;
      min-height: 0;
    }
    .added-products {
      max-height: max(180px, 30dvh);
      padding: 4px;
      margin: 0 -4px 8px;
    }
    .confirmation-discount.compact {
      padding-block: 4px;
    }

    .recommended-product {
      grid-template-columns: 104px minmax(0, 1fr);
      grid-template-areas: 'image details' 'actions actions';
      grid-template-rows: auto auto;
      gap: 12px 10px;
    }
    .recommendation-visual {
      width: 104px;
      height: 104px;
      border-radius: 1px 0 0 0;
    }
    .recommendation-details {
      gap: 3px;
      padding: 10px 10px 0 0;
    }
    .recommendation-title {
      font-size: 20px;
    }
    .recommendation-label {
      font-size: 13px;
      padding: 3px 10px;
    }
    .recommendation-points {
      max-height: 120px;
      font-size: 13px;
    }
    .recommendation-mobile-actions {
      grid-area: actions;
      align-items: stretch;
      width: 100%;
      margin: 0;
      padding: 0 12px 6px;
    }
    .recommendation-mobile-actions .recommendation-action {
      width: 100%;
    }

    .dialog-actions {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 8px;
      padding: 8px 12px 16px;
      padding-bottom: max(16px, env(safe-area-inset-bottom));

      .v-btn {
        width: 100%;
        min-width: 0;
        padding: 0 8px;
        font-size: 12px;
        letter-spacing: 0;
      }

      .configure-again {
        grid-column: 1 / -1;
        font-size: 10px;
      }
    }
  }
}

.replacement-dialog {
  .v-label {
    min-width: 0;
    flex: 1;
  }
  .replacement-position {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 2fr);
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: 6px;
    padding-block: 4px;
  }
  .replacement-images {
    display: flex;
    min-width: 0;
    gap: 4px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    .v-image {
      flex: 0 0 48px;
    }
  }
  .replacement-quantity {
    color: rgba($shop-black, .6);
  }
  .replacement-header {
    flex-wrap: nowrap;
    gap: 12px;
    justify-content: space-between;
    word-break: normal;
    .v-btn {
      flex-shrink: 0;
    }
  }
  .v-card__actions {
    gap: 8px;
    .v-btn {
      flex: 1 1 0;
      min-width: 0;
      max-width: 100%;
      height: auto;
      min-height: 36px;
      margin: 0;
      padding: 8px;
      letter-spacing: 0;
      font-size: 12px;
    }
    .v-btn ::v-deep .v-btn__content {
      white-space: normal;
    }
  }
}

@keyframes sheet-enter {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .added-to-basket-dialog {
    animation: none;
    transition: none;
  }
}
</style>
